import React, { useState, useEffect } from 'react';
import { usersAPI, establishmentAPI, employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import {
  UserPlus, Mail, Shield, Building2, RefreshCw,
  CheckCircle, XCircle, Clock, Save, X, Edit2, Trash2,
  UserCheck, AlertCircle, Key, Users,
} from 'lucide-react';
import './InviteUsers.css';

// ── Multi-select organization picker ─────────────────────────────────────────
const MultiOrgSelect = ({ establishments, selectedIds, onChange, style }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = React.useRef(null);
  const btnRef = React.useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // When opening, flip upward if there isn't enough space below the button.
  // Dropdown is ~200px max-height; need ~220px of headroom below to open down.
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < 220 && spaceAbove > spaceBelow);
  }, [open]);

  const toggle = (id) => {
    const idStr = String(id);
    onChange(selectedIds.includes(idStr) ? selectedIds.filter(x => x !== idStr) : [...selectedIds, idStr]);
  };

  const selectedNames = establishments
    .filter(e => selectedIds.includes(String(e.id)))
    .map(e => e.name);

  return (
    <div ref={ref} className="iu-multi-select" style={style}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="iu-multi-select-btn"
      >
        <span className="iu-multi-select-text">
          {selectedIds.length === 0 ? '— Select organizations —' : `${selectedIds.length} selected`}
        </span>
        <span className="iu-multi-select-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {selectedIds.length > 0 && (
        <div className="iu-multi-select-chips">
          {selectedNames.map(name => (
            <span key={name} className="iu-multi-select-chip">{name}</span>
          ))}
        </div>
      )}

      {open && (
        <div className={`iu-multi-select-dropdown${openUp ? ' iu-multi-select-dropdown-up' : ''}`}>
          {establishments.length === 0 && (
            <div className="iu-multi-select-empty">No organizations found</div>
          )}
          {establishments.map(est => (
            <label
              key={est.id}
              className={`iu-multi-select-option${selectedIds.includes(String(est.id)) ? ' selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(String(est.id))}
                onChange={() => toggle(est.id)}
                style={{ accentColor: 'var(--theme-primary, #2563eb)' }}
              />
              <span>{est.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Role badge ────────────────────────────────────────────────────────────────
const RoleBadge = ({ role }) => (
  <span className="iu-role-badge">{role || '—'}</span>
);

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    ACTIVE:           { cls: 'iu-status-active',   label: 'Active'   },
    PENDING_APPROVAL: { cls: 'iu-status-pending',  label: 'Pending'  },
    INACTIVE:         { cls: 'iu-status-inactive', label: 'Inactive' },
  };
  const st = map[status] || { cls: 'iu-status-inactive', label: status || 'Unknown' };
  return <span className={`iu-status-badge ${st.cls}`}>{st.label}</span>;
};

// ── Multi-org display for table rows ─────────────────────────────────────────
const MultiOrgDisplay = ({ userId, establishments }) => {
  const [estIds, setEstIds] = useState(null);

  useEffect(() => {
    usersAPI.getUserEstablishments(userId)
      .then(r => setEstIds(r.establishment_ids || []))
      .catch(() => setEstIds([]));
  }, [userId]);

  if (estIds === null) return <span className="iu-state-body">Loading…</span>;
  if (estIds.length === 0) return <span className="iu-state-body">No orgs assigned</span>;

  const names = estIds.map(id => {
    const est = establishments.find(e => e.id === id);
    return est ? est.name : `Org #${id}`;
  });

  return (
    <div className="iu-org-chips">
      <Building2 size={13} style={{ color: 'var(--theme-textSecondary)', flexShrink: 0 }} />
      {names.map(name => (
        <span key={name} className="iu-org-chip">{name}</span>
      ))}
    </div>
  );
};

// ── Inline role/org assignment row (SuperAdmin only) ──────────────────────────
const AssignRow = ({ user, establishments, onSave, onCancel }) => {
  const [role,         setRole]         = useState(user.role || 'Member');
  const [estId,        setEstId]        = useState(user.establishment_id ? String(user.establishment_id) : '');
  const [multiEstIds,  setMultiEstIds]  = useState([]);
  const [origEstIds,   setOrigEstIds]   = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [saveErr,      setSaveErr]      = useState('');

  useEffect(() => {
    if (user.role === 'MultiClientAdmin') {
      usersAPI.getUserEstablishments(user.id)
        .then(r => {
          const ids = (r.establishment_ids || []).map(String);
          setMultiEstIds(ids);
          setOrigEstIds(ids);
        })
        .catch(() => {});
    }
  }, [user.id, user.role]);

  const handleSave = async () => {
    if (role === 'ClientAdmin' && !estId) {
      setSaveErr('Select an organization for ClientAdmin.');
      return;
    }
    if (role === 'MultiClientAdmin' && multiEstIds.length === 0) {
      setSaveErr('Select at least one organization for MultiClientAdmin.');
      return;
    }
    setSaving(true); setSaveErr('');
    try {
      await usersAPI.update(user.id, {
        role,
        establishment_id: (role === 'ClientAdmin' || role === 'Member') && estId ? Number(estId) : null,
      });
      if (role === 'MultiClientAdmin') {
        const toAdd    = multiEstIds.filter(id => !origEstIds.includes(id));
        const toRemove = origEstIds.filter(id => !multiEstIds.includes(id));
        await Promise.all([
          ...toAdd.map(id => usersAPI.assignEstablishment(user.id, Number(id))),
          ...toRemove.map(id => usersAPI.removeEstablishment(user.id, Number(id))),
        ]);
      }
      onSave();
    } catch (e) {
      setSaveErr(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="iu-table-edit-row">
      <td>
        <div className="iu-user-name">{user.username}</div>
        <div className="iu-user-meta">{user.email}</div>
      </td>
      <td>
        <select
          value={role}
          onChange={e => {
            setRole(e.target.value);
            if (e.target.value === 'SuperAdmin') { setEstId(''); setMultiEstIds([]); }
          }}
          className="iu-input"
          style={{ width: 'auto', minWidth: '160px' }}
        >
          {['Member', 'ClientAdmin', 'MultiClientAdmin', 'SuperAdmin'].map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </td>
      <td colSpan={2}>
        {(role === 'ClientAdmin' || role === 'Member') ? (
          <select
            value={estId}
            onChange={e => setEstId(e.target.value)}
            className="iu-input"
            style={{ width: 'auto', minWidth: '200px' }}
          >
            <option value="">— Select organization —</option>
            {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        ) : role === 'MultiClientAdmin' ? (
          <MultiOrgSelect
            establishments={establishments}
            selectedIds={multiEstIds}
            onChange={setMultiEstIds}
            style={{ minWidth: '240px' }}
          />
        ) : (
          <span className="iu-org-all">All organizations</span>
        )}
        {saveErr && <div className="iu-field-error">{saveErr}</div>}
      </td>
      <td>
        <div className="iu-assign-row-actions">
          <button onClick={handleSave} disabled={saving} className="iu-btn iu-btn-primary">
            <Save size={13} />{saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onCancel} className="iu-btn iu-btn-secondary">
            <X size={13} /> Cancel
          </button>
        </div>
      </td>
    </tr>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const InviteUsers = () => {
  const { user: currentUser } = useAuth();
  const lockedEstId   = useActiveEstId();
  const isSuperAdmin  = currentUser?.role === 'SuperAdmin';
  const isClientAdmin = currentUser?.role === 'ClientAdmin';
  const isAdmin       = ['SuperAdmin', 'ClientAdmin', 'MultiClientAdmin'].includes(currentUser?.role);

  const [users,          setUsers]          = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [editingId,      setEditingId]      = useState(null);
  const [deletingId,     setDeletingId]     = useState(null);
  const [actionMsg,      setActionMsg]      = useState('');

  const [showCreate,       setShowCreate]       = useState(false);
  const [createEmail,      setCreateEmail]      = useState('');
  const [createUsername,   setCreateUsername]   = useState('');
  const [createPassword,   setCreatePassword]   = useState('');
  const [createRole,       setCreateRole]       = useState('ClientAdmin');
  const [createEstId,      setCreateEstId]      = useState('');
  const [createMobile,     setCreateMobile]     = useState('');
  const [createEmployeeId, setCreateEmployeeId] = useState('');
  const [createEstIds,     setCreateEstIds]     = useState([]);
  const [estEmployees,     setEstEmployees]     = useState([]);
  const [creating,         setCreating]         = useState(false);
  const [createMsg,        setCreateMsg]        = useState('');
  const [createErr,        setCreateErr]        = useState('');

  const fetchAll = async () => {
    setLoading(true); setError('');
    try {
      const [usersData, estData] = await Promise.all([
        usersAPI.list(),
        establishmentAPI.getAll(),
      ]);
      setUsers(usersData.users || []);
      setEstablishments(estData.establishments || []);
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (createRole === 'Member' && createEstId) {
      employeeAPI.getByEstablishment(createEstId)
        .then(r => setEstEmployees(r.employees || []))
        .catch(() => setEstEmployees([]));
    } else {
      setEstEmployees([]);
      setCreateEmployeeId('');
    }
  }, [createRole, createEstId]);

  const estName = (id) => {
    const e = establishments.find(e => e.id === id);
    return e ? e.name : id ? `Org #${id}` : '—';
  };

  const visibleUsers = lockedEstId
    ? users.filter(u => String(u.establishment_id) === String(lockedEstId))
    : users;
  const pending = visibleUsers.filter(u => u.status === 'PENDING_APPROVAL' || u.approved === 'No');
  const active  = visibleUsers.filter(u => u.status !== 'PENDING_APPROVAL' && u.approved !== 'No');

  const handleApprove = async (userId) => {
    try {
      await usersAPI.approve(userId);
      notifySavedAndReload('User approved successfully.');
      return;
      fetchAll();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  const handleReject = async (userId) => {
    try {
      await usersAPI.delete(userId);
      notifySavedAndReload('Registration request rejected.');
      return;
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await usersAPI.delete(userId);
      notifySavedAndReload('User deleted.');
      return;
    } catch (e) {
      alert(e.message || 'Failed to delete user');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createEmail && !createMobile) {
      setCreateErr('Please enter an email address or mobile number.');
      return;
    }
    if ((createRole === 'ClientAdmin' || createRole === 'Member') && !createEstId) {
      setCreateErr('Please select an organization.');
      return;
    }
    if (createRole === 'MultiClientAdmin' && createEstIds.length === 0) {
      setCreateErr('Please select at least one organization.');
      return;
    }
    if (createRole === 'Member' && !createEmployeeId) {
      setCreateErr('Please select an employee to link this account to.');
      return;
    }
    setCreating(true); setCreateMsg(''); setCreateErr('');
    try {
      const result = await usersAPI.invite(
        createEmail,
        createRole,
        createRole !== 'SuperAdmin' && createRole !== 'MultiClientAdmin' && createEstId ? Number(createEstId) : null,
        createUsername || null,
        createPassword || null,
        createRole === 'Member' ? createEmployeeId : null,
        createMobile || null,
      );

      if (createRole === 'MultiClientAdmin' && createEstIds.length > 0) {
        const newUserId = result?.user_id || result?.id;
        if (newUserId) {
          await Promise.all(createEstIds.map(id => usersAPI.assignEstablishment(newUserId, Number(id))));
        }
      }

      notifySavedAndReload(`Account created for ${createEmail || createMobile}.`);
      return;
    } catch (e) {
      setCreateErr(e.message || 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const totalUsers   = visibleUsers.length;
  const pendingCount = pending.length;
  const activeCount  = active.length;

  return (
    <div className="iu-page">

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="iu-header">
        <div className="iu-header-left">
          <div className="iu-header-icon">
            <Shield size={20} color="#fff" />
          </div>
          <div>
            <h2 className="iu-header-title">Users & RBAC</h2>
            <p className="iu-header-subtitle">Manage user accounts and role assignments</p>
          </div>
        </div>
        <div className="iu-header-actions">
          {isAdmin && (
            <button
              className={`iu-btn ${showCreate ? 'iu-btn-secondary' : 'iu-btn-primary'}`}
              onClick={() => { setShowCreate(!showCreate); setCreateMsg(''); setCreateErr(''); }}
            >
              <UserPlus size={14} />
              {showCreate ? 'Cancel' : 'Add User'}
            </button>
          )}
          <button className="iu-btn iu-btn-secondary" onClick={fetchAll}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Stats ────────────────────────────────────────────────────── */}
      {!loading && (
        <div className="iu-stats">
          <div className="iu-stat-card">
            <div className="iu-stat-label"><Users size={13} /> Total Users</div>
            <div className="iu-stat-value">{totalUsers}</div>
          </div>
          <div className="iu-stat-card">
            <div className="iu-stat-label"><Clock size={13} /> Pending Approval</div>
            <div className="iu-stat-value">{pendingCount}</div>
          </div>
          <div className="iu-stat-card">
            <div className="iu-stat-label"><UserCheck size={13} /> Active Users</div>
            <div className="iu-stat-value">{activeCount}</div>
          </div>
        </div>
      )}

      {/* ── Action message ────────────────────────────────────────────────── */}
      {actionMsg && (
        <div className={`iu-msg ${actionMsg.startsWith('Error') ? 'iu-msg-error' : 'iu-msg-success'}`}>
          <span>{actionMsg}</span>
          <button className="iu-msg-close" onClick={() => setActionMsg('')}><X size={14} /></button>
        </div>
      )}

      {/* ── Create User Form ─────────────────────────────────────────────── */}
      {isAdmin && showCreate && (
        <div className="iu-card">
          <div className="iu-card-header">
            <div className="iu-card-header-left">
              <div className="iu-card-header-icon">
                <UserPlus size={15} />
              </div>
              <div>
                <h3 className="iu-card-title">Add User Account</h3>
                <p className="iu-card-subtitle">Create a portal account. Employees register themselves via the sign-up page.</p>
              </div>
            </div>
          </div>
          <form onSubmit={handleCreate} className="iu-form-body">
            <div className="iu-form-grid">
              <div className="iu-form-field">
                <label className="iu-label">Username</label>
                <input
                  type="text" required
                  value={createUsername} onChange={e => setCreateUsername(e.target.value)}
                  placeholder="username" className="iu-input"
                />
              </div>
              <div className="iu-form-field">
                <label className="iu-label"><Mail size={12} /> Email</label>
                <input
                  type="email"
                  value={createEmail} onChange={e => setCreateEmail(e.target.value)}
                  placeholder="admin@company.com" className="iu-input"
                />
              </div>
              <div className="iu-form-field">
                <label className="iu-label">Mobile</label>
                <input
                  type="tel"
                  value={createMobile} onChange={e => setCreateMobile(e.target.value)}
                  placeholder="+91 98765 43210" className="iu-input"
                />
              </div>
              <div className="iu-form-field">
                <label className="iu-label"><Key size={12} /> Password</label>
                <input
                  type="password" required
                  value={createPassword} onChange={e => setCreatePassword(e.target.value)}
                  placeholder="Set password" className="iu-input"
                />
              </div>
              <div className="iu-form-field">
                <label className="iu-label"><Shield size={12} /> Role</label>
                <select
                  value={createRole}
                  onChange={e => { setCreateRole(e.target.value); setCreateEstId(''); setCreateEstIds([]); setCreateEmployeeId(''); }}
                  className="iu-input"
                >
                  <option value="Member">Member</option>
                  {isSuperAdmin && <option value="ClientAdmin">ClientAdmin</option>}
                  {isSuperAdmin && <option value="MultiClientAdmin">MultiClientAdmin</option>}
                  {isSuperAdmin && <option value="SuperAdmin">SuperAdmin</option>}
                </select>
              </div>
              {(createRole === 'ClientAdmin' || createRole === 'Member') && (
                <div className="iu-form-field">
                  <label className="iu-label">
                    <Building2 size={12} /> Organization <span className="iu-required">*</span>
                  </label>
                  <select
                    value={createEstId} onChange={e => setCreateEstId(e.target.value)}
                    className="iu-input"
                  >
                    <option value="">— Select —</option>
                    {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              )}
              {createRole === 'MultiClientAdmin' && (
                <div className="iu-form-field">
                  <label className="iu-label">
                    <Building2 size={12} /> Organizations <span className="iu-required">*</span>
                  </label>
                  <MultiOrgSelect
                    establishments={establishments}
                    selectedIds={createEstIds}
                    onChange={setCreateEstIds}
                  />
                </div>
              )}
              {createRole === 'Member' && createEstId && (
                <div className="iu-form-field">
                  <label className="iu-label">Employee <span className="iu-required">*</span></label>
                  <select
                    value={createEmployeeId} onChange={e => setCreateEmployeeId(e.target.value)}
                    className="iu-input"
                  >
                    <option value="">— Select employee —</option>
                    {estEmployees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="iu-form-footer">
              <button type="submit" disabled={creating} className="iu-btn iu-btn-primary">
                <Save size={14} />{creating ? 'Creating…' : 'Create User'}
              </button>
              {createMsg && <span className="iu-inline-msg iu-inline-msg-success">{createMsg}</span>}
              {createErr && <span className="iu-inline-msg iu-inline-msg-error">{createErr}</span>}
            </div>
          </form>
        </div>
      )}

      {/* ── Pending Approvals ─────────────────────────────────────────────── */}
      {(loading || pending.length > 0) && (
        <div className="iu-card">
          <div className="iu-card-header">
            <div className="iu-card-header-left">
              <div className="iu-card-header-icon">
                <AlertCircle size={15} />
              </div>
              <div>
                <h3 className="iu-card-title">
                  Pending Approval
                  {pending.length > 0 && (
                    <span className="iu-pending-badge" style={{ marginLeft: '0.5rem' }}>
                      {pending.length}
                    </span>
                  )}
                </h3>
                <p className="iu-card-subtitle">Users awaiting account approval</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="iu-state-center">
              <div className="iu-spinner" />
              <p className="iu-state-body">Loading…</p>
            </div>
          ) : pending.length === 0 ? (
            <div className="iu-state-center">
              <div className="iu-state-icon"><CheckCircle size={22} /></div>
              <p className="iu-state-title">All clear</p>
              <p className="iu-state-body">No pending registration requests.</p>
            </div>
          ) : (
            <div className="iu-table-wrap">
              <table className="iu-table">
                <thead>
                  <tr>
                    {['User', 'Organization', 'Registered', 'Actions'].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pending.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div className="iu-user-name">{u.username}</div>
                        {u.email  && <div className="iu-user-meta">{u.email}</div>}
                        {u.mobile && <div className="iu-user-meta">{u.mobile}</div>}
                      </td>
                      <td>
                        <div className="iu-org-cell">
                          <Building2 size={13} />
                          {estName(u.establishment_id)}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--theme-textSecondary)' }}>
                        {u.created ? new Date(u.created).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div className="iu-actions-cell">
                          <button className="iu-btn iu-btn-approve" onClick={() => handleApprove(u.id)}>
                            <UserCheck size={12} /> Approve
                          </button>
                          <button className="iu-btn iu-btn-reject" onClick={() => handleReject(u.id)}>
                            <XCircle size={12} /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Active Users ──────────────────────────────────────────────────── */}
      <div className="iu-card">
        <div className="iu-card-header">
          <div className="iu-card-header-left">
            <div className="iu-card-header-icon">
              <Users size={15} />
            </div>
            <div>
              <h3 className="iu-card-title">
                {isClientAdmin ? 'Organization Users' : 'All Users'}
              </h3>
              {isSuperAdmin && (
                <p className="iu-hint">Click Assign to change a user's role or organization.</p>
              )}
            </div>
          </div>
          {active.length > 0 && (
            <span className="iu-count-badge">
              {active.length} user{active.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loading ? (
          <div className="iu-state-center">
            <div className="iu-spinner" />
            <p className="iu-state-body">Loading users…</p>
          </div>
        ) : error ? (
          <div className="iu-state-center">
            <div className="iu-state-icon"><AlertCircle size={22} /></div>
            <p className="iu-state-title" style={{ color: '#b91c1c' }}>{error}</p>
          </div>
        ) : active.length === 0 ? (
          <div className="iu-state-center">
            <div className="iu-state-icon"><Users size={22} /></div>
            <p className="iu-state-title">No active users</p>
            <p className="iu-state-body">Create a user account to get started.</p>
          </div>
        ) : (
          <div className="iu-table-wrap">
            <table className="iu-table">
              <thead>
                <tr>
                  {['User', 'Role', 'Organization', 'Status', 'Actions'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.map((u, i) =>
                  editingId === u.id ? (
                    <AssignRow
                      key={u.id}
                      user={u}
                      establishments={establishments}
                      onSave={() => { setEditingId(null); fetchAll(); }}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <tr key={u.id}>
                      <td>
                        <div className="iu-user-name">{u.username}</div>
                        {u.email && u.email !== u.username && (
                          <div className="iu-user-meta">{u.email}</div>
                        )}
                      </td>
                      <td><RoleBadge role={u.role} /></td>
                      <td>
                        {u.role === 'MultiClientAdmin' ? (
                          <MultiOrgDisplay userId={u.id} establishments={establishments} />
                        ) : u.establishment_id ? (
                          <div className="iu-org-cell">
                            <Building2 size={13} />
                            {estName(u.establishment_id)}
                          </div>
                        ) : u.role === 'SuperAdmin' ? (
                          <span className="iu-org-all">All organizations</span>
                        ) : (
                          <span style={{ color: 'var(--theme-textSecondary)' }}>—</span>
                        )}
                      </td>
                      <td><StatusBadge status={u.status} /></td>
                      <td>
                        {deletingId === u.id ? (
                          <div className="iu-delete-confirm">
                            <span className="iu-delete-confirm-text">Delete?</span>
                            <button onClick={() => handleDelete(u.id)} className="iu-btn iu-btn-sm-danger">Yes</button>
                            <button onClick={() => setDeletingId(null)} className="iu-btn iu-btn-sm-neutral">No</button>
                          </div>
                        ) : (
                          <div className="iu-actions-cell">
                            {isSuperAdmin && (
                              <button onClick={() => setEditingId(u.id)} className="iu-btn iu-btn-assign">
                                <Edit2 size={11} /> Assign
                              </button>
                            )}
                            {isAdmin && String(u.id) !== String(currentUser?.userId) && (
                              <button onClick={() => setDeletingId(u.id)} className="iu-btn iu-btn-danger">
                                <Trash2 size={11} /> Delete
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default InviteUsers;
